import { ArrowUpRight } from "lucide-react";
import { cn } from "../../utils/cn";
import { useTheme } from "../../context/ThemeContext";
import { useLocale } from "../../context/LocaleContext";
import { STRINGS } from "../../data/strings";
import { CardBody, CardContainer, CardItem } from "../atoms/effects/ThreeDCard";
import { Tag } from "../atoms/Tag";
import { ProjectMedia } from "../molecules/ProjectMedia";
import { ProjectLinks } from "../molecules/ProjectLinks";

export function ProjectCard({ project, onClick, className }) {
  const { theme } = useTheme();
  const { locale, t } = useLocale();
  const { title, description, media, tags, metadata } = project;
  const strings = STRINGS[locale].sections.projects;
  const cover = media?.[0];
  const links = metadata?.links;
  const titleText = t(title);

  return (
    <CardContainer className="inter-var w-full">
      <CardBody
        className={cn(
          "bg-neutral-950/80 relative group/card w-full h-auto rounded-xl p-6 sm:p-7 border transition-all cursor-pointer backdrop-blur-md flex flex-col gap-5",
          className
        )}
        style={{ borderColor: `${theme.accent}22` }}
        onClick={onClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = `0 20px 40px -10px ${theme.accent}44`;
          e.currentTarget.style.borderColor = `${theme.accent}66`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = `0 0 0px ${theme.accent}00`;
          e.currentTarget.style.borderColor = `${theme.accent}22`;
        }}
      >
        <CardItem translateZ="100" className="w-full">
          <div
            className="relative aspect-video w-full overflow-hidden rounded-xl bg-neutral-900 border transition-colors duration-500"
            style={{ borderColor: `${theme.accent}11` }}
          >
            <ProjectMedia
              item={cover}
              alt={titleText}
              autoPlay={cover?.type === "video"}
              className="rounded-xl transition-all duration-700 group-hover/card:scale-105 group-hover/card:opacity-95"
              onError={(e) => {
                e.target.src =
                  "https://placehold.co/600x400/1a1a1a/ffffff?text=" +
                  encodeURIComponent(titleText);
              }}
            />
            {cover?.type === "video" && (
              <span
                className="pointer-events-none absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white border backdrop-blur-md bg-black/50"
                style={{ borderColor: `${theme.accent}55` }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: theme.accent }}
                />
                Video
              </span>
            )}
          </div>
        </CardItem>

        <CardItem
          translateZ="50"
          as="h3"
          className="text-xl sm:text-2xl font-bold leading-tight transition-colors duration-300"
          style={{ color: "white" }}
        >
          {titleText}
        </CardItem>

        <CardItem
          as="p"
          translateZ="40"
          className="text-neutral-400 text-sm sm:text-base leading-relaxed line-clamp-2 -mt-2"
        >
          {t(description)}
        </CardItem>

        <CardItem translateZ={20} className="flex flex-wrap gap-2">
          {tags.slice(0, 3).map((tag) => (
            <Tag key={tag} size="md">
              {tag}
            </Tag>
          ))}
        </CardItem>

        <CardItem
          translateZ={30}
          className="mt-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t w-full"
          style={{ borderColor: `${theme.accent}1a` }}
        >
          <ProjectLinks
            links={links}
            size="sm"
            variant="icon"
            strings={strings}
          />
          <button
            type="button"
            onClick={onClick}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 hover:scale-[1.03] active:scale-[0.97] w-full sm:w-auto"
            style={{
              backgroundColor: theme.accent,
              color: "black",
              boxShadow: `0 0 15px ${theme.accent}55`,
            }}
          >
            <span>{strings.viewProject}</span>
            <ArrowUpRight size={16} />
          </button>
        </CardItem>
      </CardBody>
    </CardContainer>
  );
}
